import { useMemo, useRef } from 'react';
import { Box, Stack, TableCell } from '@material-ui/core';
import { fNumber } from 'utils/formatNumber';
import { PATH_DASHBOARD } from 'routes/paths';
import {
  preloadContactDetail,
  createHoverIntentPreloader
} from '../../detail/data/sessionCache';
import {
  useOpenContactDetail,
  preloadContactDetailViewChunks
} from '../../detail/ContactDetailView';
import {
  getContactCrmIsHot,
  getContactCrmStatus,
  getContactCustomerTypes,
  getContactOriginLabel
} from '../../../../../@types/sharedSchema';
import { useAvailableColumns } from 'hooks/useAvailableColumns';
import { ContactTableDateTime } from './ContactTableDateTime';
import { ContactTableCustomerChip } from './ContactTableCustomerChip';
import { ContactCrmStatusChip } from './ContactCrmStatusChip';
import { ContactTablePhoneNumber } from './ContactTablePhoneNumber';
import { ContactTableAiFieldFlag } from './ContactTableAiFieldFlag';
import { getContactTableFixedColumns } from 'utils/contacts/columns';
import { clampLineStyle } from 'utils/listings/display';
import { ContactTableProperty } from './ContactTableProperty';
import { ContactTableTag } from './ContactTableTags';
import type { RealEstateContact } from '../../../../../@types/sharedSchema';
import type { ActiveFixedColumns } from 'utils/contacts/columns';
import type { ActiveColumns, ContactColumns, ContactWithoutId } from 'utils/contacts/table';
import type { RealEstateTab } from '../core/ContactTableTabs';

export type ContactTableRowComponent = {
  menuButton: JSX.Element;
  checkboxButton: JSX.Element;
  listingMatchesComponent: JSX.Element;
};

type ContactTableDesktopRowProps = ContactTableRowComponent & {
  contact: RealEstateContact;
  currentTab: null | RealEstateTab;
  activeColumns: ActiveColumns;
  contactColumns: ContactColumns[];
  activeFixedColumns: ActiveFixedColumns;
};

export function ContactTableDesktopRow({
  contact,
  currentTab,
  menuButton,
  activeColumns,
  contactColumns,
  checkboxButton,
  activeFixedColumns,
  listingMatchesComponent
}: ContactTableDesktopRowProps): JSX.Element {
  const availableColumns = useAvailableColumns({ activeColumns, contactColumns });

  const { parsedFixedActiveColumns } = getContactTableFixedColumns(currentTab, {
    activeFixedColumns,
    components: {
      menuButton,
      checkboxButton,
      listingMatchesComponent
    }
  });

  return (
    <>
      {parsedFixedActiveColumns.map(({ id, children }) => (
        <TableCell key={id}>{children}</TableCell>
      ))}
      {availableColumns.map(({ id }) => {
        const CellRenderer = contactCellRenderer[id];

        return (
          <TableCell key={id}>
            <CellRenderer contact={contact} />
          </TableCell>
        );
      })}
    </>
  );
}

export type CellRenderer = (props: { contact: RealEstateContact }) => JSX.Element;

type ContactCellRenderer = {
  [key in keyof ContactWithoutId]: CellRenderer;
} & { sortBy?: CellRenderer };

export const contactCellRenderer: ContactCellRenderer = {
  callerPhoneNumber: ({ contact }) => {
    return <ContactTablePhoneNumber contact={contact} />;
  },
  callerFirstName: ({ contact }) => {
    return <ContactNameLinkCell id="callerFirstName" contact={contact} />;
  },
  callerLastName: ({ contact }) => {
    return <ContactNameLinkCell id="callerLastName" contact={contact} />;
  },
  createdTime: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="createdTime"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableDateTime value={contact.createdTime} />;
        }}
      />
    );
  },
  updatedTime: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="updatedTime"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableDateTime value={contact.updatedTime} />;
        }}
      />
    );
  },
  customerType: ({ contact }) => {
    // A contact can hold several types — render one chip per type (normalized, so
    // legacy single-type docs render exactly as before).
    const contactCustomerTypes = getContactCustomerTypes(contact);
    return (
      <GenericCellRenderer
        id="customerType"
        contact={contact}
        overrideCheck={contactCustomerTypes.length > 0}
        CustomRenderer={() => {
          return (
            <Stack direction="row" flexWrap="wrap" gap={0.5} component="span">
              {contactCustomerTypes.map((type) => (
                <ContactTableCustomerChip key={type} type="customerType" customerChipType={type} />
              ))}
            </Stack>
          );
        }}
      />
    );
  },
  customerSeriousness: ({ contact }) => {
    // The column keeps its historical id, but the value it renders is the CRM v2 status
    // (crmStatus/crmSubStatus + the 🔥 flag) — never the retired customerSeriousness mirror,
    // whose default field check would hide חדש/בטיפול/מושהה (they derive no mirror at all).
    const hasAnyStatus = Boolean(getContactCrmStatus(contact) || getContactCrmIsHot(contact));
    return (
      <GenericCellRenderer
        id="customerSeriousness"
        contact={contact}
        overrideCheck={hasAnyStatus}
        CustomRenderer={() => {
          return <ContactCrmStatusChip contact={contact} />;
        }}
      />
    );
  },
  customerBudget: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="customerBudget"
        contact={contact}
        CustomRenderer={() => {
          return <>{`₪${fNumber(contact.customerBudget)}`}</>;
        }}
      />
    );
  },
  customerBudgetRent: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="customerBudgetRent"
        contact={contact}
        CustomRenderer={() => {
          return <>{`₪${fNumber(contact.customerBudgetRent)}`}</>;
        }}
      />
    );
  },
  tags: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="tags"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableTag contact={contact} />;
        }}
      />
    );
  },
  interestedInLocations: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="interestedInLocations"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableProperty type="interestedInLocations" contact={contact} />;
        }}
      />
    );
  },
  interestedInPropertyTypes: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="interestedInPropertyTypes"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableProperty type="interestedInPropertyTypes" contact={contact} />;
        }}
      />
    );
  },
  roomsMin: ({ contact }) => {
    return <GenericCellRenderer id="roomsMin" contact={contact} />;
  },
  roomsMax: ({ contact }) => {
    return <GenericCellRenderer id="roomsMax" contact={contact} />;
  },
  floorsMin: ({ contact }) => {
    return <GenericCellRenderer id="floorsMin" contact={contact} />;
  },
  floorsMax: ({ contact }) => {
    return <GenericCellRenderer id="floorsMax" contact={contact} />;
  },
  additionalNotes: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="additionalNotes"
        contact={contact}
        CustomRenderer={() => {
          return <Box sx={clampLineStyle(1)}>{contact.additionalNotes}</Box>;
        }}
      />
    );
  },
  contactOrigin: ({ contact }) => {
    // System origins are stored in English (legacy-stable ids) — display in Hebrew.
    return (
      <GenericCellRenderer
        id="contactOrigin"
        contact={contact}
        CustomRenderer={() => <>{getContactOriginLabel(contact.contactOrigin)}</>}
      />
    );
  },
  contactOriginAdditionalInfo: ({ contact }) => {
    return <GenericCellRenderer id="contactOriginAdditionalInfo" contact={contact} />;
  },
  lastBulkMessageReceivedTime: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="lastBulkMessageReceivedTime"
        contact={contact}
        CustomRenderer={() => {
          return <ContactTableDateTime value={contact.lastBulkMessageReceivedTime} />;
        }}
      />
    );
  },
  optedOutOfBulkMessages: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="optedOutOfBulkMessages"
        contact={contact}
        overrideCheck={typeof contact.optedOutOfBulkMessages === 'boolean'}
        CustomRenderer={() => {
          return <>{contact.optedOutOfBulkMessages ? 'Yes' : 'No'}</>;
        }}
      />
    );
  },
  isInSpamDatabase: ({ contact }) => {
    return (
      <GenericCellRenderer
        id="isInSpamDatabase"
        contact={contact}
        overrideCheck={typeof contact.isInSpamDatabase === 'boolean'}
        CustomRenderer={() => {
          return <>{contact.isInSpamDatabase ? 'Yes' : 'No'}</>;
        }}
      />
    );
  }
};

// Name cells double as the "open full page" affordance (עמוד לקוח): a plain
// left-click opens the customer page IN-PLACE over the list (?contact= param —
// the table stays mounted underneath, back is instant); it's a REAL <a> to the
// standalone route, so middle-click / ctrl(cmd)-click open it in a new tab
// natively. Hovering warms the customer-page session cache + code chunks so
// the page opens instantly.
function ContactNameLinkCell({
  id,
  contact
}: {
  id: 'callerFirstName' | 'callerLastName';
  contact: RealEstateContact;
}): JSX.Element {
  const openContactDetail = useOpenContactDetail();

  // Hover-intent-gated warm-up (bare mouseenter would fire the full preload
  // fan-out on every cell a pointer sweep down the name column crosses).
  const preloadTargetRef = useRef(contact);
  preloadTargetRef.current = contact;
  const hoverPreload = useMemo(
    () =>
      createHoverIntentPreloader(() => {
        preloadContactDetail(preloadTargetRef.current);
        preloadContactDetailViewChunks();
      }),
    []
  );

  // Phone-only leads (no first AND no last name — common for WhatsApp-origin
  // contacts) still get the primary open-page affordance: the first-name cell
  // renders the phone as the link text, same as the detail view's name
  // fallback. The last-name cell keeps its plain '-' so the phone isn't shown
  // twice.
  const bothNamesEmpty = !contact.callerFirstName && !contact.callerLastName;
  const phoneFallback =
    id === 'callerFirstName' && bothNamesEmpty ? contact.id : null;
  const linkText = contact[id] || phoneFallback;

  if (!linkText) return <GenericCellRenderer id={id} contact={contact} />;

  return (
    <GenericCellRenderer
      id={id}
      contact={contact}
      overrideCheck
      CustomRenderer={() => (
        <Box
          component="a"
          href={PATH_DASHBOARD.general.contactDetail(contact.id)}
          title="עמוד לקוח"
          dir={phoneFallback ? 'ltr' : undefined}
          onMouseEnter={hoverPreload.onMouseEnter}
          onMouseLeave={hoverPreload.onMouseLeave}
          onPointerDown={hoverPreload.onPointerDown}
          onClick={(e) => {
            // The whole row also opens the customer page — stopPropagation so
            // a name click never double-triggers it (and a modified/new-tab
            // click never triggers the in-place open at all).
            e.stopPropagation();
            // Modified clicks (ctrl/cmd/shift/alt) fall through to the browser
            // — the anchor's href opens the standalone route in a new tab.
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            openContactDetail(contact.id);
          }}
          sx={{
            cursor: 'pointer',
            fontWeight: 600,
            color: 'inherit',
            textDecoration: 'none',
            '&:hover': { color: 'primary.main', textDecoration: 'underline' }
          }}
        >
          {linkText}
        </Box>
      )}
    />
  );
}

type GenericCellRendererProps = {
  id: keyof ContactWithoutId;
  contact: RealEstateContact;
  overrideCheck?: boolean;
  CustomRenderer?: () => JSX.Element;
};

function GenericCellRenderer({
  id,
  contact,
  overrideCheck,
  CustomRenderer
}: GenericCellRendererProps): JSX.Element {
  const parsedValue = contact[id];

  const isValid = overrideCheck ?? parsedValue;

  if (!isValid) return <>-</>;

  return (
    <ContactTableAiFieldFlag id={id} contact={contact}>
      {CustomRenderer ? <CustomRenderer /> : parsedValue}
    </ContactTableAiFieldFlag>
  );
}
